import React, { forwardRef, useEffect, useId, useRef, useState } from "react";
import { PdfRenderer } from "../pdf-renderer/pdf-renderer";
import type { PdfRendererHandle } from "../pdf-renderer/types";
import { FormPageOverlay, FormTool } from "./form-page-overlay";
import { PdfFormFieldEditor } from "./field-editor";
import { boxToRect, clampBox, isValidRect, rectToBox } from "./geometry";
import type {
  PdfFormField,
  PdfFormFieldEditorContext,
  PdfFormRect,
  PdfFormRendererProps,
} from "./types";
import "./pdf-form-renderer.scss";

export type {
  PdfFormRendererProps,
  PdfFormField,
  PdfFormRect,
  PdfFormValue,
  PdfFormFieldChange,
  PdfFormCoordinateOrigin,
  PdfFormFieldEditorContext,
} from "./types";
export { isPdfFormCheckboxChecked, getPdfFormTextValue } from "./field-value";

export const PdfFormRenderer = forwardRef<
  PdfRendererHandle,
  PdfFormRendererProps
>(function PdfFormRenderer(props, ref) {
  const {
    fields,
    onFieldsChange,
    readOnly = false,
    coordinateOrigin = "bottom-left",
    selectedFieldId,
    onSelectionChange,
    showFieldEditor = true,
    renderFieldEditor,
    createFieldId,
    renderPageOverlay,
    height = 600,
    width = "100%",
    className,
    style,
    ...viewerProps
  } = props;
  const locked = readOnly || !onFieldsChange;
  const [internalSelection, setInternalSelection] = useState<string | null>(
    null
  );
  const [tool, setTool] = useState<FormTool>("select");
  const viewerRef = useRef<PdfRendererHandle | null>(null);
  const sourceRef = useRef(viewerProps.src);
  const pageSizes = useRef(
    new Map<number, { width: number; height: number }>()
  );
  const prefix = useId();
  const counter = useRef(0);
  const selection =
    selectedFieldId === undefined ? internalSelection : selectedFieldId;
  const selected = fields.find((field) => field.id === selection);

  function select(id: string | null) {
    if (id !== null) setTool("select");
    if (selectedFieldId === undefined) setInternalSelection(id);
    onSelectionChange?.(id);
  }

  useEffect(() => {
    if (sourceRef.current !== viewerProps.src) {
      sourceRef.current = viewerProps.src;
      pageSizes.current.clear();
      setTool("select");
      select(null);
    }
  }, [viewerProps.src]);
  useEffect(() => {
    if (selection && !selected) select(null);
  }, [selection, selected]);
  useEffect(() => {
    if (locked) setTool("select");
  }, [locked]);

  function update(id: string, patch: Partial<Omit<PdfFormField, "id">>) {
    if (locked) return;
    const previousField = fields.find((field) => field.id === id);
    if (!previousField) return;
    let field: PdfFormField = { ...previousField, ...patch };
    if (!isValidRect(field.rect)) return;
    const size = pageSizes.current.get(field.pageIndex);
    if (size && patch.rect)
      field = {
        ...field,
        rect: boxToRect(
          clampBox(rectToBox(field.rect, size, coordinateOrigin), size),
          size,
          coordinateOrigin
        ),
      };
    onFieldsChange?.(
      fields.map((item) => (item.id === id ? field : item)),
      { type: "update", field, previousField }
    );
  }

  function remove(id: string) {
    if (locked) return;
    const field = fields.find((item) => item.id === id);
    if (!field) return;
    onFieldsChange?.(
      fields.filter((item) => item.id !== id),
      { type: "delete", field }
    );
    select(null);
  }

  function add(
    pageIndex: number,
    rect: PdfFormRect,
    type: "text" | "checkbox"
  ) {
    if (locked) return;
    let id: string;
    if (createFieldId) id = createFieldId();
    else {
      do {
        id = `${prefix}-field-${++counter.current}`;
      } while (fields.some((field) => field.id === id));
    }
    if (!id || fields.some((field) => field.id === id))
      throw new Error(
        "createFieldId must return a unique, non-empty field ID."
      );
    const field: PdfFormField = {
      id,
      pageIndex,
      rect,
      type,
      label: type === "text" ? "New text field" : "New checkbox",
      value: "",
      options: type === "checkbox" ? ["Yes"] : [],
      answerFormat: null,
    };
    onFieldsChange?.([...fields, field], { type: "add", field });
    select(id);
    setTool("select");
  }

  const editorContext: PdfFormFieldEditorContext | undefined = selected
    ? {
        field: selected,
        readOnly: locked,
        onChange: (patch) => update(selected.id, patch),
        onDelete: () => remove(selected.id),
      }
    : undefined;

  return (
    <div
      className={["pdf-form-renderer", className].filter(Boolean).join(" ")}
      style={{ height, width, ...style }}
    >
      <div className="pdf-form-renderer__workspace">
        <PdfRenderer
          {...viewerProps}
          ref={(handle) => {
            viewerRef.current = handle;
            if (typeof ref === "function") ref(handle);
            else if (ref) ref.current = handle;
          }}
          height="100%"
          width="100%"
          className="pdf-form-renderer__viewer"
          features={{
            ...viewerProps.features,
            ...(locked ? {} : { textSelection: false }),
          }}
          renderPageOverlay={(page) => {
            if (page.pdfWidth && page.pdfHeight)
              pageSizes.current.set(page.pageIndex, {
                width: page.pdfWidth,
                height: page.pdfHeight,
              });
            return (
              <>
                {renderPageOverlay?.(page)}
                <FormPageOverlay
                  key={page.pageIndex}
                  page={page}
                  fields={fields}
                  origin={coordinateOrigin}
                  selectedId={selection}
                  readOnly={locked}
                  tool={locked ? "select" : tool}
                  onSelect={select}
                  onUpdate={update}
                  onAdd={add}
                  onDelete={remove}
                  onCancel={() => {
                    setTool("select");
                    select(null);
                  }}
                />
              </>
            );
          }}
        />
        {(!locked || showFieldEditor) && (
          <aside
            className="pdf-form-renderer__sidebar"
            aria-label="Form fields"
          >
            {showFieldEditor && (
              <h2>
                Form fields <span>{fields.length}</span>
              </h2>
            )}
            {!locked && (
              <div
                className="pdf-form-renderer__tools"
                role="group"
                aria-label="Form field tools"
              >
                {(["text", "checkbox"] as const).map((item) => (
                  <button
                    type="button"
                    key={item}
                    aria-pressed={tool === item}
                    onClick={() => setTool(tool === item ? "select" : item)}
                  >
                    {item === "text" ? "Add text box" : "Add checkbox"}
                  </button>
                ))}
              </div>
            )}
            {showFieldEditor && (
              <>
                <div
                  className="pdf-form-renderer__field-list"
                  role="group"
                  aria-label="Choose a field"
                >
                  {fields.length === 0 && (
                    <p>
                      {locked
                        ? "No fields."
                        : "Add a text box or checkbox on the PDF to get started."}
                    </p>
                  )}
                  {fields.map((field) => (
                    <button
                      type="button"
                      key={field.id}
                      aria-pressed={selection === field.id}
                      onClick={() => {
                        select(field.id);
                        viewerRef.current?.goToPage(field.pageIndex + 1);
                      }}
                    >
                      <span>{field.label || "Untitled field"}</span>
                      <small>
                        Page {field.pageIndex + 1} · {field.type}
                      </small>
                    </button>
                  ))}
                </div>
                {editorContext ? (
                  renderFieldEditor ? (
                    renderFieldEditor(editorContext)
                  ) : (
                    <PdfFormFieldEditor {...editorContext} />
                  )
                ) : (
                  fields.length > 0 && (
                    <p>Select a field to view its details.</p>
                  )
                )}
              </>
            )}
          </aside>
        )}
      </div>
    </div>
  );
});
